import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { defineMessages, useIntl, type IntlShape } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import GeoContext from 'contexts/geoContext'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import { showTripPlannerSnackbar } from 'tripPlanner/components/Snackbar/TripPlannerSnackbarHost'
import type { EnrichmentStatus, ItineraryUpdate, EnrichmentCompleteEvent, TripItineraryData } from 'tripPlanner/api/types'
import { applyItineraryUpdates, applyEnrichmentUpdate } from 'tripPlanner/utils/itineraryUpdates'
import { orderTripItineraryStops } from 'tripPlanner/utils/itineraryStopOrder'
import { patchExperienceUpdatesWithPendingDaySelection } from 'tripPlanner/utils/pendingExperienceDaySelection'
import { useGetItinerary } from 'tripPlanner/hooks/api/chat'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { computeTripDatesFromStops, formatStopDateRange, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import {
  showTripActionConfirmedSnackbar,
  shouldConfirmTripAction,
} from 'tripPlanner/components/Snackbar/tripActionSnackbar'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { AddedItineraryItemLink } from 'tripPlanner/assistant/contexts/AddedItineraryItemContext'
import type { ItineraryScrollTarget } from 'tripPlanner/assistant/contexts/ItineraryContext'
import { buildFlightName } from '../utils/transportNames'

export type { ItineraryScrollTarget } from 'tripPlanner/assistant/contexts/ItineraryContext'

interface UseItineraryUpdatesArgs {
  chatId: number | undefined
  // True while the route names a chat that hasn't been copied into chatId yet, which leaves
  // the itinerary query disabled. Folded into isItineraryLoading so the first render after a
  // refresh reads as "loading", not "empty".
  isChatIdSyncPending: boolean
  // Ref to track streaming state - prevents refetchOnWindowFocus from overwriting optimistic updates mid-stream
  isStreamingRef: React.RefObject<boolean>
  // Ref to the assistant turn currently streaming, so an add can be attributed to the message
  // that caused it. A ref (not a value) because this hook runs before the chat core.
  activeAssistantMessageIdRef: React.RefObject<string | undefined>
}

interface UseItineraryUpdatesReturn {
  itineraryData: TripItineraryData | undefined
  isItineraryLoading: boolean
  enrichingStopIds: Set<number>
  enrichingItemIds: Set<number>
  scrollTarget: ItineraryScrollTarget | undefined
  clearScrollTarget: () => void
  addedItemLink: AddedItineraryItemLink | undefined
  // Streaming callbacks receive chatId as first argument to avoid stale closure issues
  handleItineraryUpdates: (chatIdArg: number, updates: Array<ItineraryUpdate>) => void
  handleEnrichmentComplete: (chatIdArg: number, event: EnrichmentCompleteEvent) => void
  handleRefetchItinerary: (chatIdArg: number) => void
  // Non-streaming callbacks use closure chatId (safe for user-initiated actions on current chat)
  refetchCurrentChatItinerary: () => void
}

// ── Deletion snackbar name lookup ─────────────────────────────────────
// After applyItineraryUpdates strips the entity from the cache the snackbar
// still needs to name it. These helpers read from the prior snapshot captured
// before the reducer ran.

function findDeletedStopName(prev: TripItineraryData | undefined, stopId: number): string | undefined {
  return prev?.stops.find(s => s.id === stopId)?.placeName
}

function findDeletedDayLabel(
  prev: TripItineraryData | undefined,
  dayId: number,
  intl: IntlShape,
): string | undefined {
  for (const stop of prev?.stops ?? []) {
    const day = stop.days.find(d => d.id === dayId)
    if (day) return getLocalisedDayLabel(intl, day.dayNumber, day.date)
  }
  return undefined
}

/**
 * Resolves where a surface should scroll for an update. stopVisits is authoritative;
 * the flat stopId/dayId pair is the deprecated fallback still sent by older streams.
 */
function resolveScrollTarget(update: ItineraryUpdate): ItineraryScrollTarget | undefined {
  const itemId = 'itemId' in update ? update.itemId : undefined

  if ('stopVisits' in update && update.stopVisits?.length > 0) {
    return { stopId: update.stopVisits[0].tripStopId, dayId: update.stopVisits[0].startDayId, itemId }
  }
  if ('stopId' in update && update.stopId !== undefined) {
    return { stopId: update.stopId, dayId: 'dayId' in update ? update.dayId : undefined, itemId }
  }
  return undefined
}

function findDeletedItemName(prev: TripItineraryData | undefined, itemId: number): string | undefined {
  for (const stop of prev?.stops ?? []) {
    for (const day of stop.days) {
      const item = day.items.find(i => i.id === itemId)
      if (item) return item.name
    }
    const item = (stop.itemsWithoutDay ?? []).find(i => i.id === itemId)
    if (item) return item.name
    for (const transport of stop.transports ?? []) {
      const option = transport.options.find(o => o.itemId === itemId)
      if (option) return buildFlightName(option, transport.name)
    }
  }
  return undefined
}

type AddedStopUpdate = Extract<ItineraryUpdate, { kind: 'stop_added' }>
type AddedItemUpdate = Extract<ItineraryUpdate, { kind: 'item_added' }>
type UpdatedStopUpdate = Extract<ItineraryUpdate, { kind: 'stop_updated' }>
type UpdatedItemUpdate = Extract<ItineraryUpdate, { kind: 'item_updated' }>

// Shared by all three confirmations, so "2 activities" reads the same however it got there.
const SNACKBAR_NOUNS = defineMessages({
  unnamedActivity: {
    id: 'HAWquKfUttGOwlqZwX8E',
    defaultMessage: 'activity',
    description: 'Fallback noun for an unnamed itinerary activity in the trip planner AI chat',
  },
  unnamedStop: {
    id: 'yRxcIING_tY4NfEYDlnm',
    defaultMessage: 'stop',
    description: 'Fallback noun for an unnamed itinerary stop in the itinerary',
  },
  unnamedDay: {
    id: 'b3OtipMxRNGXjZQyPThw',
    defaultMessage: 'day',
    description: 'Fallback noun for an itinerary day whose label cannot be resolved in the trip planner',
  },
  activityCount: {
    id: '3RV5aFDxhMvWZ2m8l-gc',
    defaultMessage: '{count, plural, one {# activity} other {# activities}}',
    description: 'Names how many activities a change affected, shown under the trip planner snackbar heading',
  },
  stopCount: {
    id: 'MoN3bR45i1a9Tuezjs5S',
    defaultMessage: '{count, plural, one {# stop} other {# stops}}',
    description: 'Names how many stops a change affected, shown under the trip planner snackbar heading',
  },
})

/** Items win over stops: asking for the Eiffel Tower can create Paris as a side effect. */
function resolveTouchedDescription(
  intl: IntlShape,
  stops: ReadonlyArray<{ stop?: { placeName?: string } }>,
  items: ReadonlyArray<{ item?: { name?: string } }>,
): string | undefined {
  if (items.length === 1) return items[0].item?.name ?? intl.formatMessage(SNACKBAR_NOUNS.unnamedActivity)
  if (items.length > 1) return intl.formatMessage(SNACKBAR_NOUNS.activityCount, { count: items.length })
  if (stops.length === 1) return stops[0].stop?.placeName ?? intl.formatMessage(SNACKBAR_NOUNS.unnamedStop)
  if (stops.length > 1) return intl.formatMessage(SNACKBAR_NOUNS.stopCount, { count: stops.length })
  return undefined
}

export function resolveAddedSnackbarDescription(
  intl: IntlShape,
  addedStops: ReadonlyArray<AddedStopUpdate>,
  addedItems: ReadonlyArray<AddedItemUpdate>,
): string | undefined {
  return resolveTouchedDescription(intl, addedStops, addedItems)
}

export function resolveUpdatedSnackbarDescription(
  intl: IntlShape,
  updatedStops: ReadonlyArray<UpdatedStopUpdate>,
  updatedItems: ReadonlyArray<UpdatedItemUpdate>,
): string | undefined {
  return resolveTouchedDescription(intl, updatedStops, updatedItems)
}

/** Prioritised stop > day > item; names read from the pre-reducer snapshot. */
export function resolveDeletedSnackbarDescription(
  intl: IntlShape,
  prevData: TripItineraryData | undefined,
  updates: ReadonlyArray<ItineraryUpdate>,
): string | undefined {
  const deletedStop = updates.find((u): u is Extract<ItineraryUpdate, { kind: 'stop_deleted' }> => u.kind === 'stop_deleted')
  if (deletedStop) {
    return findDeletedStopName(prevData, deletedStop.stopId) ?? intl.formatMessage(SNACKBAR_NOUNS.unnamedStop)
  }

  const deletedDay = updates.find((u): u is Extract<ItineraryUpdate, { kind: 'day_deleted' }> => u.kind === 'day_deleted')
  if (deletedDay) {
    return findDeletedDayLabel(prevData, deletedDay.dayId, intl) ?? intl.formatMessage(SNACKBAR_NOUNS.unnamedDay)
  }

  const deletedItem = updates.find((u): u is Extract<ItineraryUpdate, { kind: 'item_deleted' }> => u.kind === 'item_deleted')
  if (deletedItem) {
    return findDeletedItemName(prevData, deletedItem.itemId) ?? intl.formatMessage(SNACKBAR_NOUNS.unnamedActivity)
  }

  return undefined
}

// ── Enrichment derivation helpers ─────────────────────────────────────

const ENRICHING_STATUSES: ReadonlySet<EnrichmentStatus> = new Set(['pending', 'enriching'])

function isEnriching(entity: { enrichmentStatus?: EnrichmentStatus }): boolean {
  return entity.enrichmentStatus !== undefined && ENRICHING_STATUSES.has(entity.enrichmentStatus)
}

function collectEnrichingIds(
  entities: ReadonlyArray<{ id: number; enrichmentStatus?: EnrichmentStatus }>,
  ids: Set<number>,
): void {
  for (const entity of entities) {
    if (isEnriching(entity)) ids.add(entity.id)
  }
}

/**
 * Returns a referentially-stable Set that only changes identity when its contents change.
 * Prevents unnecessary re-renders of ItineraryContext consumers and refetchInterval churn
 * when itineraryData reference changes but the derived enriching IDs remain the same.
 */
function useStableSet(rawSet: Set<number>): Set<number> {
  const ref = useRef(rawSet)

  if (!setsAreEqual(rawSet, ref.current)) {
    ref.current = rawSet
  }

  return ref.current
}

function setsAreEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) {
    if (!b.has(id)) return false
  }
  return true
}

// ── Hook ──────────────────────────────────────────────────────────────

/**
 * Manages itinerary state for the chat trip planner.
 *
 * Data Flow:
 * 1. React Query (`useGetItinerary`) is the single source of truth for itinerary data
 * 2. During streaming, optimistic updates are applied via `queryClient.setQueryData`
 * 3. After stream completes, `handleRefetchItinerary` invalidates the cache to sync with server
 * 4. `refetchOnWindowFocus` is disabled during streaming to prevent overwriting optimistic updates
 *    - When not streaming, window focus triggers refetch to ensure fresh data
 *
 * Enrichment State (derived, not managed):
 * - `enrichingStopIds` / `enrichingItemIds` are derived from `enrichmentStatus` on itinerary entities
 * - Self-terminating: enrichment finishes → status becomes terminal → Sets empty → refetchInterval stops
 * - Self-resetting: chat switch → itinerary data changes → Sets reflect new chat automatically
 * - `refetchInterval` (3s) acts as a fallback when the stream closes before all enrichment completes
 *   (e.g., 15s BE timeout exceeded). Stream events handle the happy path via optimistic cache patches.
 */
export function useItineraryUpdates({
  chatId,
  isChatIdSyncPending,
  isStreamingRef,
  activeAssistantMessageIdRef,
}: UseItineraryUpdatesArgs): UseItineraryUpdatesReturn {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const eventBus = useTripPlannerEventBus()
  const { currentRegionCode } = useContext(GeoContext)

  // refetchInterval is driven by derived enrichment state. useState + sync-during-render
  // ensures the interval is current on the same render cycle that detects enriching entities,
  // avoiding the one-render lag that a ref-based approach would have.
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)

  const { data: itineraryData, isInitialLoading } = useGetItinerary(chatId, currentRegionCode, {
    refetchOnWindowFocus: () => !isStreamingRef.current,
    refetchInterval,
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false
      return error !== null && typeof error === 'object' && 'status' in error && error.status === 403
    },
    retryDelay: 1000,
  })

  // react-query reports isInitialLoading as `isLoading && isFetching`, and a disabled query
  // isn't fetching — so the render before chatId lands would otherwise read as "settled with
  // no itinerary" and flash the empty state. A new chat has no route chat id, so it stays
  // false there and the empty state still shows immediately, as it should.
  const isItineraryLoading = isInitialLoading || isChatIdSyncPending

  const orderedItineraryData = useMemo(
    () => orderTripItineraryStops(itineraryData),
    [itineraryData],
  )

  // ── Derive enriching entity Sets from itinerary data ──
  // Single pass: collects both stop and item IDs in one iteration.
  // Only 'pending' and 'enriching' are active statuses. undefined = doesn't need enrichment.

  const { stopIds: enrichingStopIdsRaw, itemIds: enrichingItemIdsRaw } = useMemo(() => {
    const stopIds = new Set<number>()
    const itemIds = new Set<number>()
    for (const stop of orderedItineraryData?.stops ?? []) {
      if (isEnriching(stop)) stopIds.add(stop.id)
      for (const day of stop.days) {
        collectEnrichingIds(day.items, itemIds)
      }
      collectEnrichingIds(stop.itemsWithoutDay ?? [], itemIds)
    }
    return { stopIds, itemIds }
  }, [orderedItineraryData])

  const enrichingStopIds = useStableSet(enrichingStopIdsRaw)
  const enrichingItemIds = useStableSet(enrichingItemIdsRaw)

  // Sync refetchInterval with derived enrichment state (setState-during-render pattern).
  // React immediately re-renders before commit, so useGetItinerary gets the correct interval.
  const nextInterval: number | false = (enrichingStopIds.size > 0 || enrichingItemIds.size > 0) ? 3000 : false
  if (nextInterval !== refetchInterval) {
    setRefetchInterval(nextInterval)
  }

  const [scrollTarget, setScrollTarget] = useState<ItineraryScrollTarget | undefined>(undefined)
  const [addedItemLink, setAddedItemLink] = useState<AddedItineraryItemLink | undefined>(undefined)

  const clearScrollTarget = useCallback(() => {
    setScrollTarget(undefined)
  }, [])

  // The auto-scroll on add only lands if a Timeline is already mounted. Re-arm the target
  // when View is used, so the surface that just switched views scrolls once it renders.
  useEventSubscription('view_itinerary_item', (event) => {
    setScrollTarget({ stopId: event.stopId, dayId: event.dayId, itemId: event.itemId })
  })

  // chatIdArg is passed from streaming code to avoid stale closure (streaming has authoritative chatId)
  const handleItineraryUpdates = useCallback((chatIdArg: number, updates: Array<ItineraryUpdate>) => {
    if (updates.length === 0) return

    const prevData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
    )
    const prevDates = computeTripDatesFromStops(prevData?.stops)
    const normalizedUpdates = patchExperienceUpdatesWithPendingDaySelection(prevData, updates)

    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
        (prev) => applyItineraryUpdates(prev, normalizedUpdates),
      )
    } catch {
      // Optimistic update failed, will be corrected by server response
    }

    const newData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
    )
    const newDates = computeTripDatesFromStops(newData?.stops)

    const tripDatesChanged = prevDates?.startDate !== newDates?.startDate || prevDates?.endDate !== newDates?.endDate

    if (tripDatesChanged) {
      const dateMessage = newDates
        ? intl.formatMessage(
            {
              id: 'OOhdWO0_xvfM9OLPUY-K',
              defaultMessage: 'Trip dates updated to {dateRange}',
              description: 'Snackbar message when trip dates are set or changed',
            },
            { dateRange: formatStopDateRange(newDates.startDate, newDates.endDate) },
          )
        : intl.formatMessage({
            id: 'forhGndOznvrIFs49brO',
            defaultMessage: 'Trip dates removed',
            description: 'Snackbar message when trip dates are cleared in the trip planner',
          })
      // Keeps its own copy, but must not outrank a confirmation the traveller asked for.
      if (shouldConfirmTripAction('update')) {
        showTripPlannerSnackbar({ description: dateMessage, kind: 'success', icon: <LineCheckCircleIcon /> })
      }
    } else {
      // One snackbar per batch, so a cascading stop delete raises "Deleted Paris", not three.
      const deletedName = resolveDeletedSnackbarDescription(intl, prevData, normalizedUpdates)

      if (deletedName !== undefined) {
        if (shouldConfirmTripAction('delete')) {
          // No View action: there is nothing left to look at.
          showTripActionConfirmedSnackbar(intl, 'delete', deletedName)
        }
        // Early return is load-bearing: it also skips the scroll-target block
        // below, because we don't want to scroll-to-new on a delete.
        return
      }

      const addedStops = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_added' }> => u.kind === 'stop_added')
      const addedItems = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_added' }> => u.kind === 'item_added')

      // Both the snackbar's View action and the in-chat link target the first item of the
      // turn. The link is attributed to the assistant message that produced the add so it
      // stays anchored to that turn as the conversation continues.
      const firstAddedTarget = addedItems.length > 0 ? resolveScrollTarget(addedItems[0]) : undefined
      const addingMessageId = activeAssistantMessageIdRef.current
      if (firstAddedTarget && addingMessageId) {
        setAddedItemLink({ messageId: addingMessageId, ...firstAddedTarget })
      }

      // The add has landed, so any drawer the traveller added from has served its purpose.
      if (addedItems.length > 0 || addedStops.length > 0) {
        eventBus.dispatch({ type: 'itinerary_item_added' })
      }

      // Every add gets the same confirmation, so View is always available.
      const addedDescription = resolveAddedSnackbarDescription(intl, addedStops, addedItems)
      const addedViewTarget = firstAddedTarget
        ?? (addedStops.length > 0 ? resolveScrollTarget(addedStops[0]) : undefined)

      if (addedDescription !== undefined) {
        if (addedViewTarget) {
          setScrollTarget(addedViewTarget)
        }

        if (shouldConfirmTripAction('add')) {
          // Only offer View when there is somewhere to go, or the action renders dead.
          showTripActionConfirmedSnackbar(intl, 'add', addedDescription, addedViewTarget && (
            () => eventBus.dispatch({ type: 'view_itinerary_item', ...addedViewTarget })
          ))
        }
        return
      }

      // Updates rank last: a `stop_updated` riding along with an add must not outrank it.
      const updatedStops = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_updated' }> => u.kind === 'stop_updated')
      const updatedItems = normalizedUpdates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_updated' }> => u.kind === 'item_updated')
      const updatedDescription = resolveUpdatedSnackbarDescription(intl, updatedStops, updatedItems)

      if (updatedDescription !== undefined) {
        const updatedViewTarget = updatedItems.length > 0
          ? resolveScrollTarget(updatedItems[0])
          : (updatedStops.length > 0 ? resolveScrollTarget(updatedStops[0]) : undefined)

        if (shouldConfirmTripAction('update')) {
          showTripActionConfirmedSnackbar(intl, 'update', updatedDescription, updatedViewTarget && (
            () => eventBus.dispatch({ type: 'view_itinerary_item', ...updatedViewTarget })
          ))
        }
      }
    }

    const firstUpdate = normalizedUpdates[0]
    const firstScrollTarget = firstUpdate ? resolveScrollTarget(firstUpdate) : undefined
    if (firstScrollTarget) {
      setScrollTarget(firstScrollTarget)
    }
  }, [queryClient, intl, currentRegionCode, eventBus, activeAssistantMessageIdRef])

  const handleEnrichmentComplete = useCallback((chatIdArg: number, event: EnrichmentCompleteEvent) => {
    // Optimistic cache patch — sets enrichmentStatus: 'enriched' on the entity so the
    // derived enriching Sets immediately exclude it. Also merges enrichment data (images, place detail).
    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg, currentRegionCode),
        (prev) => applyEnrichmentUpdate(prev, event),
      )
    } catch {
      // Enrichment update failed, will be corrected by server response
    }
  }, [queryClient, currentRegionCode])

  const handleRefetchItinerary = useCallback((chatIdArg: number) => {
    queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatIdArg) })
  }, [queryClient])

  // Wrapper for non-streaming refetch (user actions on current chat)
  const refetchCurrentChatItinerary = useCallback(() => {
    if (chatId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatId) })
    }
  }, [chatId, queryClient])

  return {
    itineraryData: orderedItineraryData,
    isItineraryLoading,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    addedItemLink,
    handleItineraryUpdates,
    handleEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  }
}
